export {
    mergeControlPaddings,
    mergeControlHeights,
    mergeTouchTargets,
    mergeAvatarSizes,
    mergeShadowAlpha,
    mergeElevations,
    mergeDurations,
    mergeIconSizes,
    mergeRadiuses,
    mergeBorders,
    mergeSpaces,
    mergeShapes
} from "./theme/shapes";

export {
    mergeTypographyTokens,
    mergeTypography
} from "./theme/typography";

export {
    ARABIC_READING_FONT_WEIGHTS,
    ARABIC_FALLBACK_FONT_WEIGHT,
    ARABIC_READING_FONT_FAMILY,
    ARABIC_LINE_HEIGHT_RATIOS,
    getLocaleTypography,
    getArabicFontWeight,
    ARABIC_FONT_WEIGHTS,
    ARABIC_FONT_FAMILY,
    ARABIC_FONT_SCALE,
    getWebFontFamily,
    isReadingVariant,
    TYPOGRAPHY_GRID,
    isArabicLocale
} from "./theme/localeTypography";

export type {
    ArabicFontWeightConstantType,
    LocaleTypographyConstantType,
    ArabicTypographyRoleType,
    ArabicLocaleConstantType
} from "./theme/localeTypography";

export {
    mergePalettes,
    mergeTheme
} from "./theme/palette";

export {
    getFieldFocusStyle,
    getFocusRingStyle,
    STATE_COLOR_KEYS,
    getStateColorKey
} from "./theme/state";

export type {
    FieldFocusStyleConstantType,
    FocusRingStyleConstantType,
    StateColorKeyConstantType,
    TypedStateColorKey,
    StateColorType,
    StateColorKey
} from "./theme/state";

export {
    DISABLED_COLOR_ROLES,
    SELECTED_COLOR_ROLES,
    getColorRoleValue,
    getTypeColorRoles,
    TYPE_COLOR_ROLES,
    getTypeColors
} from "./theme/typeColors";

export type {
    SystemDisabledOverlayColorRoleType,
    SystemDisabledContentColorRoleType,
    SystemDisabledBorderColorRoleType,
    SystemOnContainerColorRoleType,
    SystemFillOverlayColorRoleType,
    ContentContainerColorRoleType,
    TypeColorRolesConstantType,
    ColorRoleValueConstantType,
    ContentBorderColorRoleType,
    ContentTextColorRoleType,
    ContentIconColorRoleType,
    SystemLowColorRoleType,
    TypeColorsConstantType,
    TypeColorRolesType,
    ContainerColorRole,
    TypeColorVariant,
    BorderColorRole,
    TitleColorRole,
    TypeColorsType,
    IconColorRole,
    ColorRole
} from "./theme/typeColors";

export {
    getElevationLayers,
    getElevationStyle,
    getSurfaceStyle,
    SURFACE_LEVELS
} from "./theme/elevation";

export type {
    ElevationConstantType,
    SurfaceConstantType,
    ElevationLevelType,
    SurfaceLevelType,
    SurfaceType
} from "./theme/elevation";

export {
    concentric
} from "./theme/concentric";

export type {
    ConcentricConstantType,
    ConcentricInsetType
} from "./theme/concentric";

export {
    getIsCornerShapeSupported,
    getCornersStyle,
    getCornerStyle
} from "./theme/corner";

export type {
    CornersStyleConstantType,
    CornerStyleConstantType,
    CornerRadiiType,
    CornerKeyType
} from "./theme/corner";

export {
    CONTROL_STEP_DOWN_SIZES,
    getControlMeasures,
    splitControlStyle,
    CONTROL_SIZES,
    getHitArea
} from "./theme/control";

export {
    subscribePointerType,
    usePointerType,
    getPointerType,
    useBreakpoint,
    getBreakpoint
} from "./pointer";

export {
    MOTION_ICON_APPEAR_SCALE,
    MOTION_ICON_APPEAR_BLUR,
    suppressWebTransitions,
    MOTION_SURFACE_SCALE,
    getMotionTransition,
    MOTION_CSS_EASINGS,
    MOTION_PRESS_SCALE,
    getCrossfadeStyle,
    getMotionDuration,
    useAnimatedState,
    useReducedMotion,
    getMotionTiming,
    useMotionTiming,
    getAppearStyle,
    MOTION_EASINGS,
    MOTION_INTENTS,
    MOTION_SPRINGS,
    getSlideStyle,
    usePressScale,
    usePresence
} from "./motion";

export type {
    MotionCrossfadeStyleConstantType,
    MotionAppearStyleConstantType,
    MotionSlideStyleConstantType,
    MotionTransitionConstantType,
    MotionDurationConstantType,
    MotionCrossfadeStyleType,
    MotionTimingConstantType,
    MotionProgressType,
    MotionTransition,
    MotionIntentType,
    MotionEasingType,
    MotionSpringType,
    MotionStyleType,
    MotionTiming,
    MotionIntent,
    MotionSpring,
    MotionType
} from "./motion";

export {
    getHorizontalStep,
    getDirectionSign,
    getMirrorStyle,
    isMirroredIcon,
    MIRRORED_ICONS
} from "./direction";

export type {
    HorizontalStepConstantType,
    DirectionSignConstantType,
    MirroredIconConstantType,
    MirrorStyleConstantType,
    HorizontalStepType,
    DirectionSignType
} from "./direction";

export {
    INTERACTION_STATE_DATA_KEY,
    INTERACTION_STATE_SELECTOR,
    getInteractionStateName,
    useInteractionState
} from "./interaction";

export type {
    InteractionStateNameConstantType,
    InteractionStateConstantType,
    InteractionHandlersType,
    InteractionDataSetType,
    InteractionStateName,
    InteractionStateType
} from "./interaction";

export {
    useOverlayAccessibility,
    useAnchoredPosition,
    getAnchoredPosition,
    POPOVER_PORTAL_NAME
} from "./overlay";

export type {
    OverlayAccessibilityConstantType,
    AnchoredPositionHookConstantType,
    AnchoredPositionConstantType,
    OverlayContainerPropsType,
    AnchoredPositionHookType,
    OverlayAccessibilityType,
    AnchoredPositionType,
    OverlayAlignType,
    AnchoredRectType,
    AnchoredSizeType,
    OverlaySideType
} from "./overlay";

export {
    DATE_TIME_FORMAT_OPTIONS,
    getMomentLocaleData,
    parseRRuleConfig,
    getMomentLocale,
    formatCurrency,
    formatDateTime,
    getIntlLocale,
    formatPercent,
    createMoment,
    formatNumber,
    isolateText
} from "./locale";

export type {
    DateTimeFormatPreset
} from "./locale";

export {
    getSwitcherVariants
} from "./preference";

export type {
    SwitcherVariantsConstantType,
    SwitcherVariantsType,
    SwitcherVariantType
} from "./preference";

export {
    getDeepActiveRouteName,
    ACTIVE_ROUTE_EVENTS,
    useActiveRouteName,
    getActiveRouteName,
    getNavigationState,
    getRouteTargetName,
    hasActiveRoute,
    isRouteActive
} from "./navigation";

export type {
    DeepActiveRouteNameConstantType,
    NavigationStateConstantType,
    RouteTargetNameConstantType,
    ActiveRouteConstantType,
    ActiveRouteItemType
} from "./navigation";
